<template>
  <div
    :style="cssMaxLinesVars"
   
  >
    <div class="flex justify-between gap-4">
      <MoleculePersona
        :avatar="{ text: initials, src: review.Reviewer?.Avatar }"
        :name="userName"
        :sub-name="reviewDate"
        :to="getProfilePath(review.Reviewer?.Id)"
      />

      <TRating
        v-if="review.Rating"
        :rating="review.Rating"
      />
    </div>

    <div class="pl-16 mt-2 space-y-4">
      <!-- Delivered by host -->
      <TBadge
        v-if="review.RVDelivered && review.ReviewType !== 'Automatic'"
        variant="highlight"
      >
        <TIcon :icon="['fas', 'route']" />
        {{ t('hostDelivered') }}
      </TBadge>

      <!-- Content -->
      <div>
        <div
          v-if="review.ReviewType !== 'Automatic'"
          ref="clampedDiv"
          :class="computedContentClasses"
        >
          {{ review.Content }}
        </div>
        <div
          v-else
          :class="computedContentClasses"
        >
          {{ t('ReviewContent', review.DaysBeforeTrip ?? 0) }}
        </div>
        <div v-if="isClamped && !showMoreContent">
          <TButton
            variant="link"
            @click="showMoreContent = true"
          >
            {{ t('actions.seeMore') }}
          </TButton>
        </div>
      </div>

      <!-- Photos -->
      <ClientOnly v-if="photos.length">
        <div class="grid grid-cols-[1fr_auto] gap-2 items-center">
          <TGallery :items="photos" />

          <div v-if="review.Id">
            <TMenu
             
            >
              <template #icon>
                <TIcon :icon="['fal', 'ellipsis-vertical']" />
              </template>
              <TMenuItem
                :text="t('reportPhoto')"
                icon="exclamation-triangle"
                weight="medium"
                @click="emit('report')"
              />
            </TMenu>
          </div>
        </div>
      </ClientOnly>

      <!-- Reply -->
      <div
        v-if="review.Reply"
        class="mt-8"
      >
        <MoleculePersona
          :avatar="{ text: review.Reply?.Replier?.Initials, src: review.Reply?.Replier?.Avatar }"
          :name="t('responseFrom', { userName: review.Reply?.Replier?.FirstName ?? '' })"
          :sub-name="review.Reply?.RepliedAt ? formatAsMonthYear(review.Reply?.RepliedAt) : undefined"
          :to="getProfilePath(review.Reply?.Replier?.Id)"
          size="sm"
        />

        <div class="text-gray-500 mt-2 pl-13 leading-relaxed">
          {{ review.Reply?.Content }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GalleryItem } from '~/components/_tailwind/gallery/types'
import type { AvatarSize } from '~/components/_tailwind/avatar/types'
import type { RvReviewDisplay } from './types'

const props = withDefaults(defineProps<{
  review: RvReviewDisplay
  avatarSize?: AvatarSize
  maxLines?: number
}>(), {
  avatarSize: 'sm',
})

const emit = defineEmits<{
  report: []
}>()

/** When true, full review content is shown (no line-clamp). "See more" is handled in-place. */
const showMoreContent = ref(false)

const clampedDiv = ref<HTMLDivElement>()

const isClamped = computed(() => {
  if (!clampedDiv.value) {
    return false
  }

  // scrollHeight is the total height of the content, whilst clientHeight is the height of the visible content ;)
  return clampedDiv.value.scrollHeight > clampedDiv.value.clientHeight
})

const { t } = useI18n()
const localePath = useLocalePath()
const { formatAsMonthYear } = useDates()
const { isPotentiallyABot } = useBotScore()

function getProfilePath(id?: Nullable<number>) {
  if (typeof id !== 'number') {
    return undefined
  }

  return localePath({ name: 'profile-id', params: { id }, query: isPotentiallyABot ? undefined : { cta: 'reviews' } })
}

const cssMaxLinesVars = computed(() => {
  if (!props.maxLines) {
    return {}
  }
  return {
    '--max-lines': props.maxLines,
  }
})

const initials = computed(() => props.review.Reviewer?.Initials ?? formatNameAsInitials(props.review.Reviewer?.FirstName, props.review.Reviewer?.LastName))
const userName = computed(() => formatNameAsUsername(props.review.Reviewer?.FirstName, props.review.Reviewer?.LastName))

const reviewDate = computed(() => props.review.CreatedAt ? formatAsMonthYear(props.review.CreatedAt) : null)

const computedContentClasses = computed(() => ([
  'text-gray-500 wrap-anywhere col-span-2 leading-relaxed',
  {
    'line-clamp-[var(--max-lines)]': props.maxLines && !showMoreContent.value,
  },
]))

const { getDynamicImage, presets } = useImage()
const presetSmall = presets.LANDSCAPE_SM
const presetLarge = presets.LANDSCAPE_LG

const photos = computed<GalleryItem[]>(() => {
  if (!props.review.Photos) {
    return []
  }

  return props.review.Photos
    .filter((photo) => photo?.PhotoPaths)
    .map(({ Id, PhotoPaths }) => ({
      id: Id,
      type: 'image',
      path: PhotoPaths?.Large,
      thumbnailUrl: getDynamicImage({ path: PhotoPaths?.Small, preset: presetSmall }) ?? '',
      imageUrl: getDynamicImage({ path: PhotoPaths?.Large, preset: presetLarge }) ?? '',
    }))
})
</script>

<i18n lang="json">
{
  "en": {
    "hostDelivered": "Delivered by host",
    "ReviewContent": "The host canceled one day before this reservation. This is an automated posting. | The host canceled {n} days before this reservation. This is an automated posting.",
    "responseFrom": "Response from {userName}",
    "reportPhoto": "Report photo"
  },
  "fr": {
    "hostDelivered": "Livré par l’hôte",
    "ReviewContent": "L’hôte a annulé un jour avant cette réservation. Ceci est une notification automatisée. | L’hôte a annulé {n} jours avant cette réservation. Ceci est une notification automatisée.",
    "responseFrom": "Réponse de {userName}",
    "reportPhoto": "Signaler la photo"
  }
}
</i18n>
